import * as React from 'react'
import {
  useQuery,
  useMutation,
  useInfiniteQuery,
  useQueryClient
} from 'react-query'
import { Prompt, useHistory } from 'react-router-dom'
import {
  getFeedbackInfo,
  addReply,
  uploadFile,
  feedbackRatingSave,
  getFeedbackDetail
} from '@feedback/services/index'
import styles from './index.module.scss'
import { Icon, TextareaItem, Toast } from 'antd-mobile'
import LeaveWarningModal from '@feedback/components/LeaveWarningModal'
import ReplyList, {
  IReply,
  fetchReplyList
} from '@feedback/pages/allfb/ticketDetail/components/ReplyList'
import FeedbackInfo, {
  fetchFeedbackInfo
} from '@feedback/pages/allfb/ticketDetail/components/FeedbackInfo'
import { getLangKey, getTextByKey, getTimeFormat } from '@feedback/utils/util'
import ImageUploading from 'react-images-uploading'
import { getAppConfig } from '@feedback/utils/CONST'
import {
  getCurrentVestRateIcon,
  isVestAppid
} from '@feedback/utils/siteConfig'
import VestLayout from '@feedback/pages/allfb/vest/Layout'
import clsx from 'clsx'

const vestSendIcon: string = require('@feedback/assets/vest/icons/send.svg')
const vestSendActiveIcon: string = require('@feedback/assets/vest/icons/send-active.svg')
const vestAttachIcon: string = require('@feedback/assets/vest/icons/attach.svg')

export const replyMessage = async (params: any) => {
  const { code, data, message } = (await addReply(params)) || {}
  if (code !== 0) {
    return Promise.reject(new Error(message))
  }
  return data
}

const Rating: React.FC<{
  feedback_id: string
  status?: number
  replyList?: IReply[]
}> = ({ feedback_id, status, replyList }) => {
  const langKey = getLangKey()
  const isRTL = langKey === 'ar-SA'
  const [ratingStatus, setRatingStatus] = React.useState(status || 0)

  const ratingSave = useMutation(
    async (params: any) => {
      const { code, data, message } = await feedbackRatingSave(params)
      if (code !== 0) return Promise.reject(new Error(message))
      return data
    },
    {
      onSuccess: (res, variables) => {
        if (variables.score) {
          setRatingStatus(variables.score)
        }
      },
      onError: ({ message }) => {
        message && Toast.info(`${message}`)
      }
    }
  )

  return (
    <div className={styles.rating}>
      <div className='tips line' dir={isRTL ? 'rtl' : 'ltr'}>
        {getTextByKey('Are you satisfied with our service')}
      </div>
      <div className='emoji-wrap'>
        {Array.from(Array(5)).map((_, index) => (
          <i
            className={`emoji level-${index + 1} ${
              ratingStatus === 0 || ratingStatus === index + 1
                ? 'is-active'
                : ''
            }`}
            key={index}
            onClick={() => {
              const customerServiceRepUID = replyList
                ?.map?.(({ user_id, uid }) => user_id || uid)
                ?.filter?.((item) => item)
                ?.pop?.()

              ratingSave.mutate({
                feedback_id,
                score: index + 1,
                ...(customerServiceRepUID
                  ? { user_id: customerServiceRepUID }
                  : {})
              })
            }}
          />
        ))}
      </div>
      {Boolean(ratingStatus) && (
        <div className='tips footer' dir={isRTL ? 'rtl' : 'ltr'}>
          {getTextByKey(
            'Thank you for your feedback! We will continue to improve our service'
          )}
        </div>
      )}
    </div>
  )
}

const TicketDetail: React.FC = () => {
  const queryClient = useQueryClient()
  const langKey = getLangKey()
  const isRTL = langKey === 'ar-SA'

  const { feedback_id, spm_error } = Object.fromEntries(
    new URLSearchParams(window.location.href.split('?')[1])
  )

  const feedbackInfo = useQuery(['getFeedbackInfo', feedback_id], () =>
    fetchFeedbackInfo({ feedback_id })
  )

  const replyList = useQuery<{ list: IReply[]; total: number }, Error>(
    ['getReplyList', feedback_id],
    () => fetchReplyList({ feedback_id })
  )

  const pushReply = useMutation(replyMessage, {
    onSuccess: async (data, variables) => {
      if (variables?.reply_content) {
        setMessage('')
      }
      queryClient.invalidateQueries('getReplyList').then(() => {
        window.scrollTo(0, Number(targetRef?.current?.scrollHeight))
      })
      if (Number(feedbackInfo.data?.feedback_status) === 2) {
        queryClient.invalidateQueries('getFeedbackInfo')
      }
    },
    onError: async ({ message }: any) => {
      message && Toast.info(`${message}`)
    }
  })

  const imageUpload = useMutation(
    async (params: any) => {
      const { code, data, message } = await uploadFile(params)
      let msg = getTextByKey('Image uploading failed')
      if (code !== 0) {
        // 图片审核
        if (code === 160001) {
          msg = getTextByKey('Image reviewing failed')
          return Promise.reject(new Error(msg))
        }
        msg += message ? ` ${message}` : ''
        return Promise.reject(new Error(message))
      }
      return data
    },
    {
      onSuccess: async (data) => {
        pushReply.mutate({
          feedback_id,
          push_user_type: 1,
          image_upload_key: data.upload_key
        })
      },
      onError: async ({ message }: Error) => {
        Toast.info(`${message}`)
      }
    }
  )

  const history = useHistory()
  const { appid } = getAppConfig() || {}
  const isVest = isVestAppid(appid)
  const targetRef = React.useRef<HTMLDivElement>(null)
  const [message, setMessage] = React.useState('')
  const [messageLimit, setMessageLimit] = React.useState(5000)
  const [isConfirmLeave, setConfirmLeave] = React.useState(false)
  const [isRatingFooter, setRatingFooter] = React.useState(false)
  const [modal, setModal] = React.useState({
    open: false,
    message: '',
    cancelText: '',
    onCancel: () => {},
    confirmText: '',
    onConfirm: () => {}
  })

  React.useEffect(() => {
    if (isConfirmLeave) {
      history.push('/ticketList')
    }
  }, [isConfirmLeave, history])

  const onSend = async (value: string) => {
    if (!value.trim() || pushReply.isLoading) return
    if (value.length > messageLimit) {
      Toast.info(
        'The text you entered is too long, please send it after cutting it down'
      )
      return
    }
    pushReply.mutate({
      feedback_id,
      push_user_type: 1,
      reply_content: value,
      spm_error
    })
  }

  const alert = (value: any) => {
    setModal({
      ...value,
      onCancel: () => {
        if (value?.onCancel()) {
          setModal((val) => ({ ...val, open: false }))
        }
      },
      onConfirm: () => {
        if (value?.onConfirm()) {
          setModal((val) => ({ ...val, open: false }))
        }
      }
    })
  }

  const canReply = Number(feedbackInfo.data?.feedback_status) < 3
  const hasMessage = Boolean(message.trim())
  const navTitle = feedbackInfo.data?.created_at
    ? getTimeFormat(feedbackInfo.data.created_at)
    : getTextByKey('feedback history')

  const onRateService = () => {
    if (Number(feedbackInfo?.data?.feedback_status) >= 2) {
      return
    }
    if (isRatingFooter) {
      Toast.info(
        getTextByKey(
          'You already rated this customer service less than {hour} hours ago'
        ).replace('{hour}', 24)
      )
      return
    }
    setRatingFooter(true)
    setTimeout(() => {
      window.scrollTo(0, Number(targetRef?.current?.scrollHeight))
    })
  }

  const scrollContent = (
    <section className={styles.content}>
      <FeedbackInfo feedback_id={feedback_id} />

      {!replyList?.isLoading && feedbackInfo?.data?.created_at && (
        <div
          className='tips'
          style={isVest ? undefined : { margin: '28px 24px' }}
          dir={langKey === 'ar-SA' ? 'rtl' : 'ltr'}
        >
          {getTextByKey(
            'A customer service representative will reply within {hour} hours'
          ).replace('{hour}', 48)}
        </div>
      )}

      <ReplyList feedback_id={feedback_id} alert={alert} />

      {isRatingFooter && (
        <Rating
          feedback_id={feedback_id}
          status={feedbackInfo.data?.cur_score}
          replyList={replyList?.data?.list}
        />
      )}

      {pushReply.isLoading && (
        <div className={'faq-list__more'}>
          <Icon type='loading' />
        </div>
      )}
    </section>
  )

  const vestFooter =
    canReply ? (
      <footer className={clsx(styles.footer, styles.vestFooter)}>
        <button
          type='button'
          className={styles.vestRateBtn}
          style={{
            opacity: Number(feedbackInfo.data?.feedback_status) >= 2 ? 0.4 : 1
          }}
          onClick={onRateService}
        >
          <img src={getCurrentVestRateIcon()} alt='' />
          <span>{getTextByKey('Rate Service')}</span>
        </button>

        <div className={styles.vestTextboxRow}>
          <div
            className={clsx(
              styles.vestTextbox,
              message.length > messageLimit - 50 && styles.hasCount
            )}
            data-value={message}
            dir={langKey === 'ar-SA' ? 'rtl' : 'ltr'}
          >
            <TextareaItem
              value={message}
              onChange={(value) => setMessage(value as string)}
              autoHeight
              placeholder='Please enter'
            />
            {message.length > messageLimit - 50 && (
              <div className='count'>
                <span
                  className={`${
                    message.length > messageLimit ? 'error-message' : ''
                  }`}
                >
                  {message.length}
                </span>
                /<span>{messageLimit}</span>
              </div>
            )}
            <ImageUploading
              maxNumber={1}
              value={[]}
              acceptType={['jpg', 'jpeg', 'png', 'gif']}
              onChange={(value) => imageUpload.mutate({ files: value })}
              onError={(errors) => {
                if (errors?.acceptType) {
                  Toast.info(
                    getTextByKey(
                      'Please check if the image format is .JPG or .PNG'
                    )
                  )
                }
              }}
            >
              {({ onImageUpload }) => (
                <button
                  type='button'
                  className={styles.vestAttach}
                  aria-label='Add attachment'
                  onClick={onImageUpload}
                >
                  <img src={vestAttachIcon} alt='' />
                </button>
              )}
            </ImageUploading>
          </div>
          <button
            type='button'
            className={clsx(styles.vestSend, hasMessage && styles.isActive)}
            aria-label='Send'
            disabled={!hasMessage || pushReply.isLoading}
            onClick={() => onSend(message)}
          >
            <img src={hasMessage ? vestSendActiveIcon : vestSendIcon} alt='' />
          </button>
        </div>
      </footer>
    ) : null

  const legacyFooter =
    canReply ? (
      <footer className={clsx(styles.footer)}>
        <div className='service-wrap'>
          <div
            className='item'
            style={{
              opacity:
                Number(feedbackInfo.data?.feedback_status) >= 2 ? 0.4 : 1
            }}
            onClick={onRateService}
          >
            <i className='icon-rating' />
            <span>{getTextByKey('Rate Service')}</span>
          </div>
        </div>

        <div className='input-wrap'>
          <div
            className={`textarea stacked ${
              message.length > messageLimit - 50 ? 'has-count' : ''
            }`}
            data-value={message}
            dir={langKey === 'ar-SA' ? 'rtl' : 'ltr'}
          >
            <TextareaItem
              value={message}
              onChange={(value) => setMessage(value as string)}
              autoHeight
            />
            {message.length > messageLimit - 50 && (
              <div className='count'>
                <span
                  className={`${
                    message.length > messageLimit ? 'error-message' : ''
                  }`}
                >
                  {message.length}
                </span>
                /<span>{messageLimit}</span>
              </div>
            )}
          </div>

          <div className='button-wrap'>
            <div className='button btn-send' onClick={() => onSend(message)}>
              <i className='send' />
            </div>
            <ImageUploading
              maxNumber={1}
              value={[]}
              acceptType={['jpg', 'jpeg', 'png', 'gif']}
              onChange={(value) => imageUpload.mutate({ files: value })}
              onError={(errors) => {
                if (errors?.acceptType) {
                  Toast.info(
                    getTextByKey(
                      'Please check if the image format is .JPG or .PNG'
                    )
                  )
                }
              }}
            >
              {({ onImageUpload }) => (
                <div className='button btn-add' onClick={onImageUpload}>
                  <i className='add' />
                </div>
              )}
            </ImageUploading>
          </div>
        </div>
      </footer>
    ) : null

  const prompts = (
    <>
      <Prompt
        message={() => {
          if (!isConfirmLeave && message.trim()) {
            setModal({
              open: true,
              message: getTextByKey(
                'You will not be able to receive a reply after it is closed'
              ),
              cancelText: getTextByKey('leave'),
              onCancel: () => setConfirmLeave(true),
              confirmText: getTextByKey('stay'),
              onConfirm: () => setModal({ ...modal, open: false })
            })
            return false
          }
          return true
        }}
      />
      <LeaveWarningModal {...modal} />
    </>
  )

  if (isVest) {
    return (
      <>
        <VestLayout
          showBottomBar={false}
          title={navTitle}
          onBack={() => history.push('/ticketList')}
          className={clsx(styles.vestShell, 'vest-dialog-shell')}
          bodyClassName={styles.vestBody}
          bottomSlot={vestFooter}
        >
          <section
            className={clsx(styles.container, styles.vest)}
            ref={targetRef}
            dir={'ltr'}
          >
            {scrollContent}
          </section>
        </VestLayout>
        {prompts}
      </>
    )
  }

  return (
    <>
      <section
        className={clsx(styles.container, styles?.[appid])}
        ref={targetRef}
        dir={'ltr'}
      >
        <div
          className={clsx(styles.goback)}
          onClick={() => history.push('/ticketList')}
        >
          <i />
        </div>

        {scrollContent}
        {legacyFooter}
      </section>
      {prompts}
    </>
  )
}

export default TicketDetail
